import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Sprout, AlertTriangle } from 'lucide-react';
type Lead = {
  name: string;
  company: string;
  priority: string;
  rating: number;
};
const warmLeads: Lead[] = [{
  name: 'Fang',
  company: 'Solve Tx',
  priority: 'Isotope Supply',
  rating: 5
}, {
  name: 'Seon A',
  company: 'SK Bio',
  priority: 'Isotope Supply',
  rating: 5
}, {
  name: 'Maria',
  company: 'Novartis',
  priority: 'Sci. Translation',
  rating: 4
}, {
  name: 'Andreas',
  company: 'Actithera',
  priority: 'Sci. Translation',
  rating: 4
}, {
  name: 'Jeff',
  company: 'Ironfist',
  priority: 'Scientific Expertise',
  rating: 4
}, {
  name: 'George',
  company: 'Blue Earth',
  priority: 'Scientific Expertise',
  rating: 4
}, {
  name: 'Verena',
  company: 'ITM',
  priority: 'Sci. Translation',
  rating: 4
}, {
  name: 'Deb',
  company: 'Photinia',
  priority: 'Isotope Supply',
  rating: 4
}];
const nurtureLeads: Lead[] = [{
  name: 'Benjamin',
  company: 'Darya',
  priority: 'Speed & Response',
  rating: 3
}, {
  name: 'Thomas',
  company: 'Full-Life',
  priority: 'Quality / GMP',
  rating: 3
}, {
  name: 'Philippe',
  company: 'Full-Life',
  priority: 'Speed & Response',
  rating: 3
}, {
  name: 'Sascha',
  company: 'Nuclidium',
  priority: 'Supply Chain',
  rating: 2
}];
function ratingStyle(r: number) {
  if (r >= 5) return 'bg-emerald-500 text-white';
  if (r >= 4) return 'bg-minerva-blue text-white';
  if (r >= 3) return 'bg-amber-400 text-white';
  return 'bg-rose-500 text-white';
}
function LeadRow({
  lead,
  i,
  delay
}: {
  lead: Lead;
  i: number;
  delay: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: delay + i * 0.04
  }} className="flex items-center gap-3 bg-white rounded-lg border border-slate-100 px-3 py-2" data-id="element-900">
      <div className="w-[120px] shrink-0" data-id="element-901">
        <div className="text-sm font-bold text-slate-800 leading-tight" data-id="element-902">
          {lead.name}
        </div>
        <div className="text-[11px] text-slate-400 leading-tight" data-id="element-903">
          {lead.company}
        </div>
      </div>
      <div className="flex-1 min-w-0" data-id="element-904">
        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-905">
          {lead.priority}
        </span>
      </div>
      <div className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-sm font-bold ${ratingStyle(lead.rating)}`} title={`Rates Minerva ${lead.rating}/5`} data-id="element-906">
        {lead.rating}
      </div>
    </motion.div>;
}
export function SlideViewerResponse() {
  const gaps = [{
    label: 'Quality / GMP',
    detail: 'Deepest hole — anonymous rate just 2.0/5',
    who: 'Thomas'
  }, {
    label: 'Speed & Responsiveness',
    detail: 'Both rated 3/5 despite prioritising it',
    who: 'Benjamin · Philippe'
  }, {
    label: 'Supply Chain Reliability',
    detail: 'Rated 2/5 yet ranks it #1 — clear disconnect',
    who: 'Sascha'
  }];
  return <div className="flex flex-col h-full max-w-[1700px] mx-auto w-full" data-id="element-907">
      <div className="mb-6 border-b border-slate-100 pb-5" data-id="element-908">
        <div className="text-sm font-bold text-snakker-purple uppercase tracking-widest mb-2" data-id="element-909">
          06 / Viewer Response Intelligence
        </div>
        <h2 className="text-4xl font-display font-bold text-slate-900 mb-2" data-id="element-910">
          Every lead has a name, a priority, and a rating
        </h2>
        <p className="text-lg text-slate-500 max-w-4xl" data-id="element-911">
          The individual Viewer Response data turns 12 leads into a targeting
          map —{' '}
          <span className="font-semibold text-slate-700" data-id="element-912">
            8 of 12 rated Minerva ≥ 4 across all three perception dimensions.
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1 items-stretch" data-id="element-913">
        {/* Lead matrix */}
        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-914">
          {/* Warm */}
          <div className="bg-emerald-50/50 rounded-2xl border border-emerald-100 p-4 flex flex-col" data-id="element-915">
            <div className="flex items-center gap-2 mb-3" data-id="element-916">
              <div className="p-1.5 bg-emerald-100 rounded-lg" data-id="element-917">
                <Flame className="w-4 h-4 text-emerald-600" data-id="element-918" />
              </div>
              <h3 className="text-base font-bold text-slate-800" data-id="element-919">
                High-Intent · Warm Conversion
              </h3>
              <span className="ml-auto text-xs font-bold text-emerald-600 bg-white px-2 py-0.5 rounded-full border border-emerald-100" data-id="element-920">
                8 leads
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed" data-id="element-921">
              Priorities align with what they already believe Minerva delivers.
            </p>
            <div className="space-y-2 flex-1" data-id="element-922">
              {warmLeads.map((l, i) => <LeadRow key={l.name} lead={l} i={i} delay={0.2} data-id="element-923" />)}
            </div>
          </div>

          {/* Nurture */}
          <div className="bg-amber-50/50 rounded-2xl border border-amber-100 p-4 flex flex-col" data-id="element-924">
            <div className="flex items-center gap-2 mb-3" data-id="element-925">
              <div className="p-1.5 bg-amber-100 rounded-lg" data-id="element-926">
                <Sprout className="w-4 h-4 text-amber-600" data-id="element-927" />
              </div>
              <h3 className="text-base font-bold text-slate-800" data-id="element-928">
                Nurture · Targeted Follow-up
              </h3>
              <span className="ml-auto text-xs font-bold text-amber-600 bg-white px-2 py-0.5 rounded-full border border-amber-100" data-id="element-929">
                4 leads
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed" data-id="element-930">
              Priorities skew to manufacturing, speed & quality — where
              perception is weakest.
            </p>
            <div className="space-y-2" data-id="element-931">
              {nurtureLeads.map((l, i) => <LeadRow key={l.name} lead={l} i={i} delay={0.4} data-id="element-932" />)}
            </div>

            {/* Rating legend */}
            <div className="mt-auto pt-4 flex items-center gap-3 flex-wrap text-[10px] font-semibold text-slate-500" data-id="element-933">
              <span className="flex items-center gap-1.5" data-id="element-934">
                <span className="w-3 h-3 rounded bg-emerald-500" data-id="element-935" />5
              </span>
              <span className="flex items-center gap-1.5" data-id="element-936">
                <span className="w-3 h-3 rounded bg-minerva-blue" data-id="element-937" />4
              </span>
              <span className="flex items-center gap-1.5" data-id="element-938">
                <span className="w-3 h-3 rounded bg-amber-400" data-id="element-939" />3
              </span>
              <span className="flex items-center gap-1.5" data-id="element-940">
                <span className="w-3 h-3 rounded bg-rose-500" data-id="element-941" />2
              </span>
              <span className="text-slate-400 font-normal" data-id="element-942">
                = Minerva rating /5
              </span>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="lg:col-span-4 flex flex-col gap-5" data-id="element-943">
          {/* Gaps */}
          <motion.div initial={{
          opacity: 0,
          x: 16
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex-1" data-id="element-944">
            <div className="flex items-center gap-2 mb-4" data-id="element-945">
              <div className="p-1.5 bg-rose-50 rounded-lg" data-id="element-946">
                <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-947" />
              </div>
              <h3 className="text-base font-bold text-slate-800" data-id="element-948">
                Where the gaps are
              </h3>
            </div>
            <div className="space-y-3" data-id="element-949">
              {gaps.map((g, i) => <div key={i} className="border-l-2 border-rose-200 pl-3" data-id="element-950">
                  <div className="flex items-center justify-between" data-id="element-951">
                    <span className="text-sm font-bold text-slate-800" data-id="element-952">
                      {g.label}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400" data-id="element-953">
                      {g.who}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-snug" data-id="element-954">
                    {g.detail}
                  </p>
                </div>)}
            </div>

            <p className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100 leading-snug" data-id="element-955">
              These gaps are only visible because every response is tied to a
              named lead — pinpointing exactly where to focus follow-up.
            </p>
          </motion.div>
        </div>
      </div>
    </div>;
}